"use client";

import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const fullQuery = `SELECT project, SUM(hours)
FROM operations.tasks
WHERE date >= now() - INTERVAL '30 days'
GROUP BY project
ORDER BY 2 DESC;`;
  const [typed, setTyped] = useState("");
  const [showResults, setShowResults] = useState(false);
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  useEffect(() => {
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setTyped(fullQuery.slice(0, i));
      if (i >= fullQuery.length) {
        clearInterval(interval);
        setTimeout(() => setShowResults(true), 200);
      }
    }, 1400 / fullQuery.length);
    return () => clearInterval(interval);
  }, []);
  const resultRows = [{
    label: "Spreadsheet tasks",
    value: "1,240 hrs"
  }, {
    label: "Report preparation",
    value: "985 hrs"
  }, {
    label: "Data validation",
    value: "412 hrs"
  }];
  return <section data-section-id="19782" id="hero" className="relative text-foreground py-32 md:py-44 overflow-hidden dark bg-accent">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-30">
        <div className="mx-auto h-full max-w-[1320px] px-5 sm:px-8 md:px-12">
          <div className="grid h-full grid-cols-12 gap-6">
            {Array.from({
            length: 13
          }).map((_, i) => <div key={i} data-index={i} className="h-full w-px bg-border" style={{
            gridColumn: i === 12 ? "12 / span 1" : `${i + 1} / span 1`,
            justifySelf: i === 12 ? "end" : "start"
          }} />)}
          </div>
        </div>
      </div>

      <div className="relative mx-auto max-w-[1320px] px-5 sm:px-8 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          {}
          <div ref={headlineRef} className="lg:col-span-8 flex flex-col" style={{
          opacity: headlineVisible ? 1 : 0,
          transform: headlineVisible ? "translateY(0)" : "translateY(8px)",
          transition: "opacity 480ms cubic-bezier(0.2, 0.8, 0.2, 1), transform 480ms cubic-bezier(0.2, 0.8, 0.2, 1)"
        }}>
            <span className="font-mono text-[11px] uppercase text-muted-foreground mb-8" style={{
            letterSpacing: "0.12em",
            lineHeight: 1
          }}>DATA AUTOMATION &amp; TECHNOLOGY</span>

            <h1 className="font-default font-semibold text-foreground" style={{
            fontSize: "clamp(2.75rem, 6.5vw, 5.75rem)",
            letterSpacing: "-0.04em",
            lineHeight: 0.96
          }}>Automate repetitive work.</h1>

            <p className="mt-8 max-w-[560px] text-primary-foreground" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            letterSpacing: "-0.005em",
            lineHeight: 1.55
          }}>Improve accuracy.<div className="text-primary-foreground">Save time.</div><div><br /></div><div className="!w-[650px]">We help businesses reduce manual effort by automating repetitive tasks, connecting information, and creating practical technology solutions that make daily workflows faster, clearer, and more reliable.</div></p>

            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
              <Button asChild className="h-12 px-6 bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-medium text-[15px] shadow-md" style={{
              letterSpacing: "-0.005em"
            }}>
                <Link to="/GetInTouchPage">Discuss Automation Needs</Link>
              </Button>

              <Link to="/OurServicesPage" className="group inline-flex items-center gap-2 text-foreground text-[15px] font-default">
                <span className="border-b border-muted-foreground pb-1 transition-colors duration-200 group-hover:border-primary">Explore Our Approach</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.25} />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-4 mt-12 lg:mt-0 w-full">
            <div className="bg-card border border-border rounded-lg p-6 w-full shadow-lg">
              {}
              <div className="flex items-center justify-between border-b border-border pb-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="block h-2 w-2 rounded-lg bg-muted-foreground/40" />
                  <span className="block h-2 w-2 rounded-lg bg-muted-foreground/40" />
                  <span className="block h-2 w-2 rounded-lg bg-muted-foreground/40" />
                </div>
                <span className="font-mono text-[11px] uppercase text-muted-foreground" style={{
                letterSpacing: "0.12em",
                lineHeight: 1
              }}>AUTOMATION_FLOW</span>
              </div>

              {}
              <pre className="font-mono text-[13px] text-foreground whitespace-pre-wrap break-words" style={{
              lineHeight: 1.5
            }}>
                <span className="text-muted-foreground">{"$ "}</span>
                <span>INPUT recurring_tasks 
READ source_files 
CHECK manual_steps 
BUILD automated_flow 
RUN validation_checks 
OUTPUT reliable_results</span>
                <span className="inline-block w-2 h-4 align-middle bg-primary ml-0.5 animate-pulse" />
              </pre>

              {}
              <div className="mt-5 pt-5 border-t border-border space-y-2 transition-opacity duration-500" style={{
              opacity: showResults ? 1 : 0
            }}>
                {resultRows.map((row, i) => <div key={row.label} data-index={i} className="flex items-center justify-between font-mono text-[13px]" style={{
                lineHeight: 1.5
              }}>
                    <span className="text-muted-foreground">{row.label}</span>
                    <span className="text-foreground">{row.value}</span>
                  </div>)}

                <div className="mt-4 pt-3 border-t border-border flex items-center justify-between font-mono text-[13px]" style={{
                lineHeight: 1.5
              }}>
                  <span className="text-muted-foreground">→ workflow ready</span>
                  <span className="text-muted-foreground">112ms</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}